import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Icon, IconButton, PrimaryButton, SearchBox, TextField, Checkbox, TooltipHost } from "@fluentui/react";
import { t } from "i18next";
import themeContext from "../../../../context/theme/ThemeContext";
import toastContext from "../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../constants/Constants";
import ModalMain from "../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../components/Modal/ModalFooter";
import ConfirmModal from "../../../../components/Modal/ConfirmModal";
import CustomMultiSelect from "../../../../components/CustomMultiSelect";
import { getLibraryCatalog, loadLibraryCatalog, saveLibraryCatalog } from "./libraryTagStore";
import "./libraryTag.css";

const UNCATEGORIZED_ID = "uncategorized";
const nameTipStyles = { root: { display: "inline-flex", maxWidth: "100%", minWidth: 0, verticalAlign: "middle" } };

const NameTip = ({ id, name, className, style, hostStyle, children }) => {
  const full = name == null ? "" : String(name);
  const styles = hostStyle ? { root: { ...nameTipStyles.root, ...hostStyle } } : nameTipStyles;
  return (
    <TooltipHost content={full} id={id} styles={styles}>
      <span className={className} style={style}>{children}</span>
    </TooltipHost>
  );
};

const checkStyles = {
  root: { margin: 0, pointerEvents: "none" },
  label: { margin: 0, padding: 0, alignItems: "center" },
  checkbox: { margin: 0 },
  text: { display: "none" },
};

const CategoryAddTag = ({ category, tags, onAttach, onRemove, renameInput, onRenameTag }) => {
  const added = [...tags]
    .filter((tag) => category.tagIds.includes(tag.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const options = [...tags]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((tag) => ({ key: tag.id, text: tag.name }));

  const setSelectedKeys = (keys) => {
    const next = new Set(keys);
    const current = new Set(category.tagIds);
    tags.forEach((tag) => {
      const wasOn = current.has(tag.id);
      const isOn = next.has(tag.id);
      if (!wasOn && isOn) onAttach(tag.id);
      if (wasOn && !isOn) onRemove(tag.id);
    });
  };

  return (
    <div className="library-manage-addbox">
      <CustomMultiSelect
        options={options}
        placeholder={t("study-list.library-tag.select-tags")}
        searchPlaceholder={t("study-list.library-tag.search-tags")}
        selectedKeys={category.tagIds}
        setSelectedKeys={setSelectedKeys}
        calloutProps={{ doNotLayer: false }}
      />
      {added.length > 0 ? (
        <div className="library-manage-tag-pills">
          {added.map((tag) => {
            const input = renameInput("tag", tag.id);
            if (input) {
              return <div key={tag.id} className="library-manage-rename">{input}</div>;
            }
            return (
              <span key={tag.id} className="library-manage-tag-pill is-static">
                <NameTip id={`library-manage-pill-name-${tag.id}`} name={tag.name}>{tag.name}</NameTip>
                <TooltipHost content={t("study-list.library-tag.rename")} id={`library-rename-pill-${tag.id}`}>
                <IconButton
                  iconProps={{ iconName: "Edit" }}
                  ariaLabel={t("study-list.library-tag.rename")}
                  styles={pillIconStyles}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => onRenameTag(tag, event)}
                />
                </TooltipHost>
                <TooltipHost content={t("study-list.library-tag.remove-tag")} id={`library-remove-pill-${tag.id}`}>
                <button type="button" className="library-manage-pill-remove" aria-label={t("study-list.library-tag.remove-tag")} onClick={() => onRemove(tag.id)}>
                  <Icon iconName="Cancel" styles={{ root: { fontSize: 10 } }} />
                </button>
                </TooltipHost>
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const errorText = (code) => {
  if (!code) {
    return "";
  }
  return t(`study-list.library-tag.${code}`);
};

const pillIconStyles = {
  root: {
    width: 16,
    height: 16,
    color: "inherit",
    backgroundColor: "transparent",
  },
  rootHovered: {
    color: "inherit",
    backgroundColor: "transparent",
  },
  icon: { fontSize: 10, color: "inherit" },
};

const ManageLibraryModal = (props) => {
  const { themeName, getThemeColor, getScrollBarStyleForTheme } = useContext(themeContext);
  const { showToastv2 } = useContext(toastContext);
  const [state, setState] = useState(getLibraryCatalog);
  const [saving, setSaving] = useState(false);
  const tempId = useRef(-1);
  const [tab, setTab] = useState("tag");
  const [newTag, setNewTag] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  const [categoryQuery, setCategoryQuery] = useState("");
  const [tagError, setTagError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [openTagId, setOpenTagId] = useState(null);
  const [openCategoryIds, setOpenCategoryIds] = useState([]);
  const [rename, setRename] = useState(null);
  const [renameError, setRenameError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const renameRef = useRef(null);
  const suppressRenameBlur = useRef(false);
  const openedSnapshot = useRef(null);

  useEffect(() => {
    if (!props.show) {
      setNewTag("");
      setNewCategory("");
      setTagError("");
      setCategoryError("");
      setRename(null);
      setRenameError("");
      renameRef.current = null;
      return undefined;
    }
    let cancelled = false;
    setNewTag("");
    setNewCategory("");
    setTagError("");
    setCategoryError("");
    loadLibraryCatalog().then((result) => {
      if (cancelled) return;
      const next = result.catalog || getLibraryCatalog();
      openedSnapshot.current = next;
      setState(next);
      renameRef.current = null;
      setRename(null);
      setRenameError("");
      setPendingDelete(null);
      if (result.error) setTagError(result.error);
    });
    return () => {
      cancelled = true;
    };
  }, [props.show]);

  const cancelModal = () => {
    props.onDismiss?.();
  };

  const saveModal = async () => {
    if (saving) return;
    let next = state;
    const pending = renameRef.current;
    if (pending && (pending.name || "").trim()) {
      const list = pending.kind === "tag" ? state.tags : state.categories;
      const unchanged = list.some((item) => String(item.id) === String(pending.id) && item.name === pending.name.trim());
      if (!unchanged) {
        const message = localNameError(pending.name, list, pending.kind, pending.id);
        if (message) {
          setRenameError(message);
          return;
        }
        const trimmed = pending.name.trim();
        next = {
          ...state,
          tags: pending.kind === "tag"
            ? state.tags.map((tag) => (String(tag.id) === String(pending.id) ? { ...tag, name: trimmed } : tag))
            : state.tags,
          categories: pending.kind === "category"
            ? state.categories.map((category) => (String(category.id) === String(pending.id) ? { ...category, name: trimmed } : category))
            : state.categories,
        };
      }
    }
    setSaving(true);
    try {
      const result = await saveLibraryCatalog(openedSnapshot.current || getLibraryCatalog(), next);
      setSaving(false);
      if (!result || result.error !== undefined) {
        const message = (result && result.error) || t("string-literals.error-msg");
        setTagError(message);
        showToastv2(TOAST_TYPE.ERROR, message);
        return;
      }
      props.onDismiss?.();
      showToastv2(TOAST_TYPE.SUCCESS, t("study-list.library-tag.catalog-saved"));
    } catch (e) {
      setSaving(false);
      showToastv2(TOAST_TYPE.ERROR, t("string-literals.error-msg"));
    }
  };

  const { tags, categories } = state;
  const renameInput = (kind, id) => {
    if (!rename || rename.kind !== kind || String(rename.id) !== String(id)) return null;
    const source = kind === "tag" ? tags : categories;
    const original = source.find((item) => String(item.id) === String(id));
    const renameChanged = !!original && (rename.name || "").trim() !== "" && (rename.name || "").trim() !== original.name;
    return (
      <>
        <TextField
          value={rename.name}
          ariaLabel={t("study-list.library-tag.rename")}
          onChange={(_, value) => onRenameChange(kind, id, value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              suppressRenameBlur.current = false;
              commitRename();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              renameRef.current = null;
              setRename(null);
              setRenameError("");
            }
          }}
          onBlur={onRenameBlur}
          errorMessage={renameError}
          styles={compactFieldStyles}
          autoFocus
        />
        <TooltipHost content={t("button.save")} id={`library-save-rename-${kind}-${id}`}>
        <IconButton
          iconProps={{ iconName: "CheckMark" }}
          ariaLabel={t("button.save")}
          disabled={!renameChanged}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            suppressRenameBlur.current = false;
            commitRename();
          }}
          styles={rowIconStyles}
        />
        </TooltipHost>
      </>
    );
  };

  const rowIconStyles = {
    root: {
      width: 28,
      height: 28,
      flexShrink: 0,
      color: "var(--text-primary)",
      backgroundColor: "transparent",
    },
    rootHovered: {
      backgroundColor: "var(--action-details-hover)",
      color: "var(--text-primary)",
    },
    icon: { fontSize: 14, color: "var(--text-primary)" },
    rootDisabled: {
      backgroundColor: "transparent",
      color: "var(--text-primary)",
      opacity: 0.35,
    },
    iconDisabled: { color: "var(--text-primary)" },
  };
  const compactFieldStyles = {
    root: { flex: 1, minWidth: 0 },
    fieldGroup: { height: 32 },
    field: { height: 32 },
  };
  const searchFieldStyles = {
    root: { width: 220, height: 32, flexGrow: 0, flexShrink: 0 },
    field: { height: 32 },
    iconContainer: { height: 32 },
  };

  const memberships = (tagId) =>
    categories.filter((category) => category.tagIds.includes(tagId));

  const filteredTags = useMemo(() => {
    const query = tagQuery.trim().toLowerCase();
    const list = [...tags].sort((a, b) => a.name.localeCompare(b.name));
    if (!query) {
      return list;
    }
    return list.filter((tag) => tag.name.toLowerCase().includes(query));
  }, [tags, tagQuery]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );

  const uncategorizedTags = useMemo(() => {
    const assigned = new Set(categories.flatMap((category) => category.tagIds));
    return [...tags]
      .filter((tag) => !assigned.has(tag.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tags, categories]);

  const categoryQueryText = categoryQuery.trim().toLowerCase();
  const tagMatchesQuery = (tag) => !categoryQueryText || tag.name.toLowerCase().includes(categoryQueryText);
  const shownUncategorized = uncategorizedTags.filter(tagMatchesQuery);
  const shownCategories = categoryQueryText
    ? sortedCategories.filter((category) => tags.some((tag) => category.tagIds.includes(tag.id) && tagMatchesQuery(tag)))
    : sortedCategories;

  const localNameError = (name, existing, kind, exceptId) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return errorText(kind === "tag" ? "tag-required" : "category-required");
    if (trimmed.length > 15) return errorText(kind === "tag" ? "tag-length" : "category-length");
    if (existing.some((item) => String(item.id) !== String(exceptId) && item.name.toLowerCase() === trimmed.toLowerCase())) {
      return errorText(kind === "tag" ? "tag-duplicate" : "category-duplicate");
    }
    return "";
  };

  const startRename = (kind, item, event) => {
    event.preventDefault();
    event.stopPropagation();
    const next = { kind, id: item.id, name: item.name };
    suppressRenameBlur.current = true;
    window.setTimeout(() => {
      suppressRenameBlur.current = false;
    }, 400);
    renameRef.current = next;
    setRenameError("");
    setRename(next);
  };

  const onRenameChange = (kind, id, value) => {
    const next = { kind, id, name: value || "" };
    renameRef.current = next;
    setRename(next);
    setRenameError("");
  };

  const onRenameBlur = () => {
    window.setTimeout(() => {
      if (suppressRenameBlur.current) return;
      const active = document.activeElement;
      if (active && active.closest && active.closest(".library-manage-rename")) return;
      commitRename();
    }, 150);
  };

  const commitRename = () => {
    const current = renameRef.current;
    if (!current) return;
    const list = current.kind === "tag" ? tags : categories;
    const unchanged = list.some((item) => String(item.id) === String(current.id) && item.name === (current.name || "").trim());
    if (unchanged) {
      renameRef.current = null;
      setRename(null);
      setRenameError("");
      return;
    }
    const message = localNameError(current.name, list, current.kind, current.id);
    if (message) {
      setRenameError(message);
      return;
    }
    const trimmed = current.name.trim();
    renameRef.current = null;
    setRename(null);
    setRenameError("");
    setState((prev) => ({
      ...prev,
      tags: current.kind === "tag"
        ? prev.tags.map((tag) => (String(tag.id) === String(current.id) ? { ...tag, name: trimmed } : tag))
        : prev.tags,
      categories: current.kind === "category"
        ? prev.categories.map((category) => (String(category.id) === String(current.id) ? { ...category, name: trimmed } : category))
        : prev.categories,
    }));
  };

  const askDelete = (kind, item, event) => {
    event.preventDefault();
    event.stopPropagation();
    const blocked = kind === "category" && item.tagIds && item.tagIds.length > 0;
    setPendingDelete({ kind, id: item.id, name: item.name, blocked });
  };

  const confirmDelete = () => {
    if (!pendingDelete || pendingDelete.blocked) {
      setPendingDelete(null);
      return;
    }
    const { kind, id } = pendingDelete;
    setState((prev) => {
      if (kind === "tag") {
        return {
          tags: prev.tags.filter((tag) => tag.id !== id),
          categories: prev.categories.map((category) => ({
            ...category,
            tagIds: category.tagIds.filter((tagId) => tagId !== id),
          })),
        };
      }
      return {
        ...prev,
        categories: prev.categories.filter((category) => category.id !== id),
      };
    });
    if (kind === "tag" && openTagId === id) setOpenTagId(null);
    setOpenCategoryIds((prev) => prev.filter((categoryId) => categoryId !== id));
    if (renameRef.current && renameRef.current.id === id) {
      renameRef.current = null;
      setRename(null);
    }
    setPendingDelete(null);
  };

  const addTag = () => {
    const message = localNameError(newTag, tags, "tag");
    if (message) {
      setTagError(message);
      return;
    }
    const tag = { id: tempId.current, name: newTag.trim(), categoryIds: [] };
    tempId.current -= 1;
    setTagError("");
    setNewTag("");
    setState((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    setOpenTagId(tag.id);
  };

  const addCategory = () => {
    const message = localNameError(newCategory, categories, "category");
    if (message) {
      setCategoryError(message);
      return;
    }
    const category = { id: tempId.current, name: newCategory.trim(), tagIds: [] };
    tempId.current -= 1;
    setCategoryError("");
    setNewCategory("");
    setState((prev) => ({ ...prev, categories: [...prev.categories, category] }));
    setOpenCategoryIds((prev) => [...prev, category.id]);
    setTab("category");
  };

  const changeTagInCategory = (tagId, categoryId, include) => {
    setState((prev) => ({
      ...prev,
      categories: prev.categories.map((category) => {
        if (category.id !== categoryId) return category;
        const has = category.tagIds.includes(tagId);
        if (include && !has) return { ...category, tagIds: [...category.tagIds, tagId] };
        if (!include && has) return { ...category, tagIds: category.tagIds.filter((id) => id !== tagId) };
        return category;
      }),
    }));
  };

  const toggleCategoryOpen = (categoryId) => {
    setOpenCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const attachExistingTag = (tagId, categoryId) => {
    changeTagInCategory(tagId, categoryId, true);
  };

  const removeTagFromCategory = (tagId, categoryId) => {
    changeTagInCategory(tagId, categoryId, false);
    showToastv2(TOAST_TYPE.SUCCESS, t("study-list.library-tag.tag-removed"));
  };

  const opened = openedSnapshot.current;
  const sameIds = (left, right) => {
    const a = [...(left || [])].map(String).sort();
    const b = [...(right || [])].map(String).sort();
    return a.length === b.length && a.every((id, index) => id === b[index]);
  };
  const draftChanged = !opened || opened.tags.length !== tags.length || opened.categories.length !== categories.length
    || tags.some((tag) => {
      const previous = opened.tags.find((item) => String(item.id) === String(tag.id));
      return !previous || previous.name !== tag.name;
    })
    || categories.some((category) => {
      const previous = opened.categories.find((item) => String(item.id) === String(category.id));
      return !previous || previous.name !== category.name || !sameIds(previous.tagIds, category.tagIds);
    });
  const pendingRenameChanged = !!rename && (rename.kind === "tag" ? tags : categories).some((item) => String(item.id) === String(rename.id) && item.name !== (rename.name || "").trim() && (rename.name || "").trim() !== "");
  const DefaultButtonList = [
    {
      title: t("button.cancel"),
      onclick: cancelModal,
    },
  ];
  const PrimaryButtonList = [
    {
      title: t("button.save"),
      onclick: saveModal,
      disabled: saving || (!draftChanged && !pendingRenameChanged),
    },
  ];

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("study-list.library-tag.manage-title")}
        handleCancel={cancelModal}
      />
    ),
    body: (
      <div
        className={`global-modal-body library-manage ${getScrollBarStyleForTheme(themeName, "common-modal-scroll")}`}
        data-theme={themeName}
        style={{ color: getThemeColor("primary-color") }}
      >
        <div className="library-manage-tabs">
          <button
            type="button"
            className={`library-manage-tab${tab === "tag" ? " is-active" : ""}`}
            aria-pressed={tab === "tag"}
            onClick={() => setTab("tag")}
          >
            {t("study-list.library-tag.by-tag")}
            <span>{tags.length}</span>
          </button>
          <button
            type="button"
            className={`library-manage-tab${tab === "category" ? " is-active" : ""}`}
            aria-pressed={tab === "category"}
            onClick={() => setTab("category")}
          >
            {t("study-list.library-tag.by-category")}
            <span>{categories.length}</span>
          </button>
        </div>

        {tab === "tag" && (
          <div>
            <div className="library-manage-toolbar">
              <SearchBox
                value={tagQuery}
                placeholder={t("study-list.library-tag.find-tag")}
                ariaLabel={t("study-list.library-tag.find-tag")}
                clearButtonProps={{ ariaLabel: t("button.clear") }}
                onChange={(_, value) => setTagQuery(value || "")}
                onClear={() => setTagQuery("")}
                styles={searchFieldStyles}
              />
              <div className="library-manage-create">
                <TextField
                  value={newTag}
                  placeholder={t("study-list.library-tag.new-tag")}
                  ariaLabel={t("study-list.library-tag.new-tag")}
                  onChange={(_, value) => {
                    setNewTag(value || "");
                    setTagError("");
                  }}
                  errorMessage={tagError}
                  styles={compactFieldStyles}
                />
                <PrimaryButton
                  text={t("study-list.library-tag.create-tag")}
                  iconProps={{ iconName: "Add" }}
                  onClick={addTag}
                  styles={{ root: { flexShrink: 0 } }}
                />
              </div>
            </div>
            {tags.length > 0 ? (
              <p className="library-manage-hint library-manage-section-hint">{t("study-list.library-tag.by-category-hint")}</p>
            ) : null}
            {tagQuery.trim() && filteredTags.length === 0 ? (
              <p className="library-manage-empty">{t("study-list.library-tag.no-tag-match", { query: tagQuery.trim() })}</p>
            ) : filteredTags.length === 0 ? null : (
              <div className="library-manage-list">
              {filteredTags.map((tag) => {
                const groups = memberships(tag.id);
                const open = openTagId === tag.id;
                const tagRename = renameInput("tag", tag.id);
                return (
                  <div key={tag.id} className="library-manage-row">
                    <div className="library-manage-row-main">
                      {tagRename ? (
                        <div className="library-manage-rename">{tagRename}</div>
                      ) : (
                        <button
                          type="button"
                          className="library-manage-row-toggle"
                          aria-expanded={open}
                          onClick={() => setOpenTagId(open ? null : tag.id)}
                        >
                          <NameTip id={`library-manage-tag-name-${tag.id}`} name={tag.name} className="library-manage-name">{tag.name}</NameTip>
                          <span className="library-manage-chips">
                            {groups.length === 0 ? (
                              <span className="library-chip library-chip-folder">
                                {t("study-list.library-tag.uncategorized")}
                              </span>
                            ) : (
                              groups.map((category) => (
                                <NameTip key={category.id} id={`library-manage-tag-cat-${tag.id}-${category.id}`} name={category.name} className="library-chip library-chip-folder">
                                  <Icon iconName="FolderHorizontal" styles={{ root: { fontSize: 12 } }} />
                                  {category.name}
                                </NameTip>
                              ))
                            )}
                          </span>
                          <span className={`library-chevron${open ? " is-open" : ""}`}>
                            <Icon iconName="ChevronRight" />
                          </span>
                        </button>
                      )}
                      <TooltipHost content={t("study-list.library-tag.rename")} id={`library-rename-tag-${tag.id}`}>
                      <IconButton
                        iconProps={{ iconName: "Edit" }}
                        ariaLabel={t("study-list.library-tag.rename")}
                        styles={rowIconStyles}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => startRename("tag", tag, event)}
                      />
                      </TooltipHost>
                      <TooltipHost content={t("study-list.library-tag.delete")} id={`library-delete-tag-${tag.id}`}>
                      <IconButton
                        iconProps={{ iconName: "Delete" }}
                        ariaLabel={t("study-list.library-tag.delete")}
                        styles={rowIconStyles}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => askDelete("tag", tag, event)}
                      />
                      </TooltipHost>
                    </div>
                    {open && (
                      <div className="library-manage-panel">
                        {categories.length === 0 ? (
                          <p className="library-manage-empty">
                            {t("study-list.library-tag.empty-categories")}
                          </p>
                        ) : (
                          <div className="library-manage-options">
                            {sortedCategories.map((category) => {
                              const on = category.tagIds.includes(tag.id);
                              return (
                                <button
                                  key={category.id}
                                  type="button"
                                  className={`library-manage-option${on ? " is-on" : ""}`}
                                  aria-pressed={on}
                                  onClick={() => changeTagInCategory(tag.id, category.id, !on)}
                                >
                                  <Checkbox checked={on} styles={checkStyles} />
                                  <NameTip id={`library-manage-option-${tag.id}-${category.id}`} name={category.name}>{category.name}</NameTip>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            )}
          </div>
        )}

        {tab === "category" && (
          <div>
            <div className="library-manage-toolbar">
              <SearchBox
                value={categoryQuery}
                placeholder={t("study-list.library-tag.find-tag")}
                ariaLabel={t("study-list.library-tag.find-tag")}
                clearButtonProps={{ ariaLabel: t("button.clear") }}
                onChange={(_, value) => setCategoryQuery(value || "")}
                onClear={() => setCategoryQuery("")}
                styles={searchFieldStyles}
              />
              <div className="library-manage-create">
                <TextField
                  value={newCategory}
                  placeholder={t("study-list.library-tag.new-category")}
                  ariaLabel={t("study-list.library-tag.new-category")}
                  onChange={(_, value) => {
                    setNewCategory(value || "");
                    setCategoryError("");
                  }}
                  errorMessage={categoryError}
                  styles={compactFieldStyles}
                />
                <PrimaryButton
                  text={t("study-list.library-tag.create-category")}
                  iconProps={{ iconName: "Add" }}
                  onClick={addCategory}
                  styles={{ root: { flexShrink: 0 } }}
                />
              </div>
            </div>
            {categories.length > 0 ? (
              <p className="library-manage-hint library-manage-section-hint">{t("study-list.library-tag.by-tag-hint")}</p>
            ) : null}
            {categoryQueryText && shownUncategorized.length === 0 && shownCategories.length === 0 ? (
              <p className="library-manage-empty">{t("study-list.library-tag.no-tag-match", { query: categoryQuery.trim() })}</p>
            ) : null}
            {shownUncategorized.length > 0 && (
              <div className="library-manage-row">
                <button
                  type="button"
                  className="library-manage-row-main"
                  aria-expanded={!!categoryQueryText || openCategoryIds.includes(UNCATEGORIZED_ID)}
                  onClick={() => toggleCategoryOpen(UNCATEGORIZED_ID)}
                >
                  <Icon iconName="Tag" />
                  <span className="library-manage-name" style={{ flex: 1, width: "auto" }}>
                    {t("study-list.library-tag.uncategorized")}
                  </span>
                  <span className="library-manage-hint">
                    {shownUncategorized.length === 1
                      ? t("study-list.library-tag.one-tag")
                      : t("study-list.library-tag.tag-count", { count: shownUncategorized.length })}
                  </span>
                  <span className={`library-chevron${categoryQueryText || openCategoryIds.includes(UNCATEGORIZED_ID) ? " is-open" : ""}`}>
                    <Icon iconName="ChevronRight" />
                  </span>
                </button>
                {(categoryQueryText || openCategoryIds.includes(UNCATEGORIZED_ID)) && (
                  <div className="library-manage-tag-pills">
                    {shownUncategorized.map((tag) => {
                      const tagRename = renameInput("tag", tag.id);
                      if (tagRename) {
                        return <div key={tag.id} className="library-manage-rename">{tagRename}</div>;
                      }
                      return (
                        <span key={tag.id} className="library-manage-tag-pill is-static">
                          <NameTip id={`library-manage-uncat-name-${tag.id}`} name={tag.name}>{tag.name}</NameTip>
                          <TooltipHost content={t("study-list.library-tag.rename")} id={`library-rename-uncat-${tag.id}`}>
                          <IconButton
                            iconProps={{ iconName: "Edit" }}
                            ariaLabel={t("study-list.library-tag.rename")}
                            styles={pillIconStyles}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => startRename("tag", tag, event)}
                          />
                          </TooltipHost>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {shownCategories.map((category) => {
              const open = !!categoryQueryText || openCategoryIds.includes(category.id);
              const categoryTags = tags.filter((tag) => category.tagIds.includes(tag.id));
              const categoryRename = renameInput("category", category.id);
              return (
                <div key={category.id} className="library-manage-row">
                  <div className="library-manage-row-main">
                    {categoryRename ? (
                      <div className="library-manage-rename">{categoryRename}</div>
                    ) : (
                      <button
                        type="button"
                        className="library-manage-row-toggle"
                        aria-expanded={open}
                        onClick={() => toggleCategoryOpen(category.id)}
                      >
                        <Icon iconName="FolderHorizontal" />
                        <NameTip
                          id={`library-manage-category-name-${category.id}`}
                          name={category.name}
                          className="library-manage-name"
                          hostStyle={{ flex: 1, minWidth: 0 }}
                          style={{ flex: 1, width: "auto" }}
                        >
                          {category.name}
                        </NameTip>
                        <span className="library-manage-hint">
                          {categoryTags.length === 1
                            ? t("study-list.library-tag.one-tag")
                            : t("study-list.library-tag.tag-count", { count: categoryTags.length })}
                        </span>
                        <span className={`library-chevron${open ? " is-open" : ""}`}>
                          <Icon iconName="ChevronRight" />
                        </span>
                      </button>
                    )}
                    <TooltipHost content={t("study-list.library-tag.rename")} id={`library-rename-category-${category.id}`}>
                    <IconButton
                      iconProps={{ iconName: "Edit" }}
                      ariaLabel={t("study-list.library-tag.rename")}
                      styles={rowIconStyles}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => startRename("category", category, event)}
                    />
                    </TooltipHost>
                    <TooltipHost content={t("study-list.library-tag.delete")} id={`library-delete-category-${category.id}`}>
                    <IconButton
                      iconProps={{ iconName: "Delete" }}
                      ariaLabel={t("study-list.library-tag.delete")}
                      styles={rowIconStyles}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => askDelete("category", category, event)}
                    />
                    </TooltipHost>
                  </div>
                  {open && (
                    <CategoryAddTag
                      category={category}
                      tags={tags}
                      onAttach={(tagId) => attachExistingTag(tagId, category.id)}
                      onRemove={(tagId) => removeTagFromCategory(tagId, category.id)}
                      renameInput={renameInput}
                      onRenameTag={(tag, event) => startRename("tag", tag, event)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    ),
    footer: (
      <ModalFooter
        PrimaryButtonList={PrimaryButtonList}
        DefaultButtonList={DefaultButtonList}
      />
    ),
    width: "760px",
    maxHeight: "80vh",
  };

  if (!props.show) {
    return null;
  }

  return (
    <>
      <ModalMain updatedModal={true} showModal={props.show} modalDetails={modalDetails} />
      <ConfirmModal
        showConfirmModal={!!pendingDelete}
        handleConfirm={confirmDelete}
        handleNotConfirm={pendingDelete && pendingDelete.blocked ? undefined : () => setPendingDelete(null)}
        handleCloseIcon={() => setPendingDelete(null)}
        title={t(pendingDelete && pendingDelete.kind === "tag" ? "study-list.library-tag.delete-tag-title" : "study-list.library-tag.delete-category-title")}
        message={pendingDelete
          ? t(
            pendingDelete.blocked
              ? "study-list.library-tag.delete-category-has-tags"
              : pendingDelete.kind === "tag"
                ? "study-list.library-tag.delete-tag"
                : "study-list.library-tag.delete-category",
            { name: pendingDelete.name }
          )
          : ""}
        confirmButtonText={pendingDelete && pendingDelete.blocked ? t("confirm-message.ok") : t("confirm-message.yes")}
        cancelButtonText={t("confirm-message.cancel")}
      />
    </>
  );
};

export default ManageLibraryModal;
